import { Trans, useTranslation } from 'react-i18next';
import { Button, Divider } from 'antd';
import { AppModal } from '@components/form/Modal';
import { AppAlert } from '@shared/components/antd';
import { AppTooltip } from '@components/app-tooltip';
import { IoIosInformationCircleOutline } from 'react-icons/io';
import { IoClose } from 'react-icons/io5';
import { RiInformationLine } from 'react-icons/ri';
import { formatAmount } from '@modules/settings/subscription/utils/utils';
import { getFormattedPrice } from '@shared/utils/price-utils';
import { capitalize } from '@shared/utils/capitalize';
import Wallet from '@/assets/static/images/wallet.png';

type Props = {
  open: boolean;
  walletLoading: boolean;
  walletBalance: number;
  totalAmount: number;
  quantity?: number | null;
  isInsufficientBalance: boolean;
  isAutoRechargeEnabled: boolean;
  tooltipMessage: string | null;
  confirming: boolean;
  onClose: VoidFunction;
  onBack: VoidFunction;
  onConfirm: VoidFunction;
  onLoadWallet: VoidFunction;
};

export function ConfirmPaymentModal({
  open,
  walletLoading,
  walletBalance,
  totalAmount,
  quantity,
  isInsufficientBalance,
  isAutoRechargeEnabled,
  tooltipMessage,
  confirming,
  onClose,
  onBack,
  onConfirm,
  onLoadWallet,
}: Props) {
  const { t } = useTranslation();

  return (
    <AppModal
      centered
      open={open}
      footer={false}
      onCancel={onClose}
      closable={false}
      classNames={{ content: 'p-0! overflow-hidden! rounded-2xl! relative! max-w-[450px]!' }}
    >
      <AppModal.Body>
        {isInsufficientBalance && (
          <AppAlert
            role="alert"
            aria-live="polite"
            type="error"
            message={
              <div className="flex items-center">
                <IoIosInformationCircleOutline className="w-5 h-5 text-red-600! flex-shrink-0 mr-3" />
                <p className="text-base text-text-600 font-normal inline-block leading-tight">
                  <Trans i18nKey="insufficient_balance_to_cover_charges">
                    Insufficient balance to cover your charges.
                  </Trans>
                </p>
              </div>
            }
            className="app-alert "
          />
        )}
        <div className="relative space-y-2">
          <Button
            onClick={onClose}
            aria-label="Close"
            className="absolute! top-2! right-2! p-1 z-10! rounded-full! w-fit text-text-400 hover:text-text-700 hover:bg-black/5 transition-colors cursor-pointer! border-none! bg-transparent"
          >
            <IoClose className="w-6! h-6!" />
          </Button>
          <div className="flex">
            <div className="relative p-5 min-w-0 pb-0!">
              <div className="flex items-center mb-4 gap-4">
                <div>
                  <p className="text-primary-500 text-sm font-medium uppercase tracking-widest">
                    {t('wallet_balance')}
                  </p>
                  <p className="text-text-800 text-4xl font-medium mt-2 leading-tight tracking-tight">
                    {walletLoading ? '—' : getFormattedPrice(walletBalance)}
                  </p>
                </div>
                <>
                  <Divider variant="solid" size="large" type="vertical" className="self-stretch  h-auto! mx-4" />
                  <div>
                    <p className="text-text-400 text-sm font-normal uppercase tracking-wide">{t('auto_recharge')}</p>
                    <div className="flex items-center gap-1 mt-2">
                      {isAutoRechargeEnabled ? (
                        <span className="inline-flex items-center px-3 py-1 rounded-full bg-emerald-50 border border-emerald-100 text-green-600 text-sm font-medium">
                          {capitalize(t('enabled'))}
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-3 py-1 rounded-full bg-gray-100  border border-gray-400 text-text-600 text-sm font-medium">
                          {capitalize(t('disabled'))}
                        </span>
                      )}
                      <AppTooltip placement="bottom" title={tooltipMessage ?? undefined}>
                        <RiInformationLine className="text-text-400" size={18} />
                      </AppTooltip>
                    </div>
                  </div>
                </>
              </div>

              <div className="grid grid-cols-[auto_auto_auto] mb-4 items-center">
                <div className="pr-10">
                  <p className="text-text-800 text-2xl font-medium leading-tight">{quantity ?? '—'}</p>
                  <p className="text-text-500 text-sm capitalize mt-1">{t('quantity')}</p>
                </div>

                <Divider type="vertical" className="self-stretch h-auto! mx-0" />

                <div className="pl-10">
                  <p className="text-text-800 text-2xl font-medium leading-tight">
                    {formatAmount(totalAmount)}
                    <span className="text-sm text-text-400 font-normal">{t('per_month_suffix')}</span>
                  </p>
                  <p className="text-text-500 text-capitalize text-base mt-1">{t('total_amount')}</p>
                </div>
              </div>

              <div className="flex gap-2">
                <AppModal.Button
                  size="middle"
                  onClick={onBack}
                  className="rounded-full! px-6! h-10! text-sm! font-medium!"
                >
                  {t('review_payment')}
                </AppModal.Button>

                {isInsufficientBalance ? (
                  <AppModal.Button
                    type="primary"
                    size="middle"
                    onClick={onLoadWallet}
                    className="rounded-full! px-6! h-10! text-sm! font-medium!"
                  >
                    {capitalize(t('load_wallet'))}
                  </AppModal.Button>
                ) : (
                  <AppModal.Button
                    type="primary"
                    size="middle"
                    loading={confirming}
                    onClick={onConfirm}
                    className="rounded-full! px-6! h-10! text-sm! font-medium!"
                  >
                    {t('confirm_payment')}
                  </AppModal.Button>
                )}
              </div>
            </div>
            <img
              src={Wallet}
              alt=""
              aria-hidden="true"
              className="absolute -bottom-5 rotate-4 -right-4 h-auto z-10 pointer-events-none sm:block hidden w-[250px]"
            />
          </div>
        </div>
      </AppModal.Body>
    </AppModal>
  );
}
